import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import { DURATION } from '@animations/durations';
import { EASE, registerEases } from '@animations/eases';
import { useAppStore } from '@stores/index';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Owns GSAP's global configuration.
 *
 * A plain class rather than a hook or a component: it has to run before
 * React mounts anything, and it must survive Fast Refresh without leaking
 * tweens or triggers.
 *
 * Scope boundary: this file configures GSAP and nothing else. Wiring
 * ScrollTrigger to Lenis belongs to ScrollManager.
 */
class AnimationManager {
  private initialized = false;

  /** Contexts handed out via createContext, reverted together on dispose. */
  private contexts = new Set<gsap.Context>();

  private mediaQuery: MediaQueryList | null = null;

  /**
   * Bound once as a class property so add/removeEventListener receive the
   * same reference. A method reference would be a new function each time
   * and removeEventListener would silently do nothing.
   */
  private readonly handleMotionPreferenceChange = (event: MediaQueryListEvent): void => {
    useAppStore.getState().setReducedMotion(event.matches);
  };

  get isInitialized(): boolean {
    return this.initialized;
  }

  /** Current OS motion preference. Safe to read before init. */
  get prefersReducedMotion(): boolean {
    return useAppStore.getState().prefersReducedMotion;
  }

  /**
   * Configures GSAP. Idempotent — Strict Mode double-invokes effects in
   * development, so this will be called twice on the first mount.
   */
  init(): void {
    if (this.initialized) return;

    gsap.registerPlugin(ScrollTrigger);
    registerEases();

    /* Project-wide tween defaults. Any tween that does not name an ease
       gets the standard arrival curve rather than GSAP's default power1. */
    gsap.defaults({
      ease: EASE.out,
      duration: DURATION.base,
    });

    /* Lag smoothing is deliberately NOT configured here. It belongs to
       whoever owns the frame loop, and ScrollManager disables it outright
       because Lenis needs a truthful clock — see the comment there. Setting
       it in both places would mean the last system to initialise silently
       wins, which is exactly the kind of ordering dependency that is
       miserable to debug. ScrollManager clamps the delta instead. */

    ScrollTrigger.config({
      /* Mobile browser chrome expanding and collapsing fires resize
         continuously. Left on, ScrollTrigger would recalculate every
         trigger mid-scroll and visibly stutter the whole page. */
      ignoreMobileResize: true,
    });

    this.syncMotionPreference();

    this.initialized = true;
  }

  /**
   * Creates a scoped GSAP context.
   *
   * Everything a component animates should go through here. Reverting a
   * context undoes every animation and trigger created inside it and
   * restores the original values, which is what makes a page unmount, or
   * a Fast Refresh, leave nothing behind.
   */
  createContext(setup: gsap.ContextFunc, scope?: Element | string | object): gsap.Context {
    const context = gsap.context(setup, scope);
    this.contexts.add(context);
    return context;
  }

  /** Reverts a context and stops tracking it. */
  revertContext(context: gsap.Context): void {
    context.revert();
    this.contexts.delete(context);
  }

  /**
   * Tears everything down.
   *
   * Order matters: ScrollTriggers are killed before contexts are reverted,
   * because a trigger firing mid-revert can write to elements that are
   * being restored and leave inconsistent inline styles behind.
   */
  dispose(): void {
    if (!this.initialized) return;

    for (const trigger of ScrollTrigger.getAll()) {
      trigger.kill();
    }

    for (const context of this.contexts) {
      context.revert();
    }
    this.contexts.clear();

    this.mediaQuery?.removeEventListener('change', this.handleMotionPreferenceChange);
    this.mediaQuery = null;

    this.initialized = false;
  }

  /** Reads the OS motion preference and keeps the store in sync with it. */
  private syncMotionPreference(): void {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    this.mediaQuery = window.matchMedia(REDUCED_MOTION_QUERY);
    useAppStore.getState().setReducedMotion(this.mediaQuery.matches);
    this.mediaQuery.addEventListener('change', this.handleMotionPreferenceChange);
  }
}

/**
 * Single shared instance. GSAP's configuration is global, so more than one
 * of these would be fighting over the same object.
 */
export const animationManager = new AnimationManager();

/* Fast Refresh replaces this module without unmounting the React tree,
   which would otherwise strand every ScrollTrigger and context created by
   the previous instance. */
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    animationManager.dispose();
  });
}
